import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, RotateCw, Info, Trash2, X, Printer, Eraser, FolderOpen, BookmarkPlus, Mail, Loader2, Sparkles, Radio, RadioTower } from 'lucide-react';
import {
  getTablePlannerData, saveTablePositions, saveRoomFixtures, saveRoomLayout,
  getRoomLayouts, emailFloorPlan, aiRoomLayout, setDefaultLayout,
  GetTablePlannerDataOutputType, GetRoomLayoutsOutputType, AiRoomLayoutOutputType,
} from 'zite-endpoints-sdk';
// ─── Types ────────────────────────────────────────────────────────────────────
type Room = GetTablePlannerDataOutputType['rooms'][0];
type Table = Room['tables'][0];
type SavedLayout = GetRoomLayoutsOutputType['layouts'][0];
type LayoutWithDefault = SavedLayout;
type Pos = { x: number; y: number; rotation: number; seatCount?: number };
type Point = { x: number; y: number };

export type FixtureType =
  | 'door' | 'fire_exit' | 'entrance' | 'staircase_up' | 'staircase_down'
  | 'bar' | 'kitchen' | 'toilets' | 'window' | 'pillar' | 'accessible_exit' | 'wall_section';

export interface Fixture {
  id: string; type: FixtureType; x: number; y: number; rotation: number;
  label?: string; w?: number; h?: number;
}

const CANVAS_W = 900;
const CANVAS_H = 560;
const CHAIR_AREA = 20; // padding around each table for chairs
const CHAIR_W = 12;
const CHAIR_H = 9;

type ChairPos = { cx: number; cy: number; angle: number };

function getChairPositions(shape: string | undefined, w: number, h: number, seatCount: number): ChairPos[] {
  if (seatCount <= 0) return [];
  const GAP = 8;
  const chairs: ChairPos[] = [];
  if (shape === 'Round' || shape === 'Oval') {
    const rx = w / 2 + GAP + CHAIR_H / 2;
    const ry = h / 2 + GAP + CHAIR_H / 2;
    for (let i = 0; i < seatCount; i++) {
      const rad = (i / seatCount) * 2 * Math.PI - Math.PI / 2;
      chairs.push({ cx: w / 2 + rx * Math.cos(rad), cy: h / 2 + ry * Math.sin(rad), angle: (i / seatCount) * 360 });
    }
  } else {
    const colSeats = Math.max(1, Math.round(w / 28));
    const rowSeats = Math.max(1, Math.round(h / 28));
    const sides = [
      { count: colSeats, cx: (t: number) => t * w, cy: () => -(GAP + CHAIR_H / 2), angle: 0 },
      { count: rowSeats, cx: () => w + GAP + CHAIR_H / 2, cy: (t: number) => t * h, angle: 90 },
      { count: colSeats, cx: (t: number) => (1 - t) * w, cy: () => h + GAP + CHAIR_H / 2, angle: 180 },
      { count: rowSeats, cx: () => -(GAP + CHAIR_H / 2), cy: (t: number) => (1 - t) * h, angle: 270 },
    ];
    let placed = 0;
    for (const side of sides) {
      for (let i = 0; i < side.count && placed < seatCount; i++, placed++) {
        const t = (i + 1) / (side.count + 1);
        chairs.push({ cx: side.cx(t), cy: side.cy(t), angle: side.angle });
      }
    }
  }
  return chairs;
}
const CLOSE_THRESHOLD = 18;
const PRINT_COLORS = ['#7B5E52','#3D7A8A','#3A7A4A','#8A6A20','#6A3D8A','#2A6A7A'];
const TABLE_COLORS = ['hsl(var(--primary))','hsl(200 60% 40%)','hsl(142 50% 35%)','hsl(35 70% 40%)','hsl(270 45% 45%)','hsl(175 50% 35%)'];

const FIXTURE_DEFS: { type: FixtureType; label: string; icon: string; defaultW: number; defaultH: number }[] = [
  { type: 'door', label: 'Door', icon: '🚪', defaultW: 50, defaultH: 50 },
  { type: 'fire_exit', label: 'Fire Exit', icon: '🚨', defaultW: 60, defaultH: 50 },
  { type: 'accessible_exit', label: 'Access Exit', icon: '♿', defaultW: 60, defaultH: 50 },
  { type: 'entrance', label: 'Entrance', icon: '⬆', defaultW: 60, defaultH: 50 },
  { type: 'staircase_up', label: 'Stairs Up', icon: '↑', defaultW: 70, defaultH: 60 },
  { type: 'staircase_down', label: 'Stairs Down', icon: '↓', defaultW: 70, defaultH: 60 },
  { type: 'bar', label: 'Bar', icon: '🍺', defaultW: 120, defaultH: 50 },
  { type: 'kitchen', label: 'Kitchen', icon: '👨‍🍳', defaultW: 100, defaultH: 70 },
  { type: 'toilets', label: 'Toilets', icon: '🚻', defaultW: 70, defaultH: 60 },
  { type: 'window', label: 'Window', icon: '🪟', defaultW: 80, defaultH: 20 },
  { type: 'pillar', label: 'Pillar', icon: '⬤', defaultW: 30, defaultH: 30 },
  { type: 'wall_section', label: 'Wall', icon: '▬', defaultW: 100, defaultH: 16 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getTableDims(shape?: string, widthCm?: number, depthCm?: number) {
  const scale = 0.65;
  const w = widthCm ? Math.max(50, Math.min(110, widthCm * scale)) : shape === 'Rectangular' ? 88 : 66;
  const h = depthCm ? Math.max(40, Math.min(110, depthCm * scale)) : (shape === 'Round' || shape === 'Oval') ? 66 : shape === 'Rectangular' ? 50 : 66;
  return { w, h };
}

function dist(a: Point, b: Point) { return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2); }

// ─── Print SVG generator ──────────────────────────────────────────────────────
function buildPrintHTML(room: Room, positions: Record<string, Pos>, fixtures: Fixture[], outline: Point[], layoutName: string): string {
  const W = CANVAS_W, H = CANVAS_H;
  const p: string[] = [];

  p.push(`<rect width="${W}" height="${H}" fill="#fafaf8"/>`);
  for (let i = 0; i * 40 <= W; i++) p.push(`<line x1="${i*40}" y1="0" x2="${i*40}" y2="${H}" stroke="#e8e8e8" stroke-width="0.5"/>`);
  for (let i = 0; i * 40 <= H; i++) p.push(`<line x1="0" y1="${i*40}" x2="${W}" y2="${i*40}" stroke="#e8e8e8" stroke-width="0.5"/>`);

  if (outline.length > 2) {
    const pts = outline.map(pp => `${pp.x},${pp.y}`).join(' ');
    p.push(`<polygon points="${pts}" fill="#f0ece6" stroke="#333" stroke-width="3" stroke-linejoin="round"/>`);
  } else if (room.widthMetres && room.lengthMetres) {
    const rw = Math.min(W - 40, room.widthMetres * 60);
    const rh = Math.min(H - 40, room.lengthMetres * 60);
    p.push(`<rect x="20" y="20" width="${rw}" height="${rh}" fill="#f0ece6" stroke="#333" stroke-width="3" rx="4"/>`);
    p.push(`<text x="24" y="16" font-size="10" fill="#888" font-family="sans-serif">${room.widthMetres}m × ${room.lengthMetres}m</text>`);
  }

  fixtures.forEach(f => {
    const w = f.w ?? 60, h = f.h ?? 50;
    const def = FIXTURE_DEFS.find(d => d.type === f.type);
    let fill = '#e0e0e0', stroke = '#aaa', textFill = '#333';
    if (['fire_exit','accessible_exit'].includes(f.type)) { fill = '#1a5c2a'; stroke = '#2a8a3a'; textFill = '#fff'; }
    else if (f.type === 'bar') { fill = '#4a2a0a'; stroke = '#8a5a20'; textFill = '#fff'; }
    else if (f.type === 'entrance') { fill = '#e8e8ff'; stroke = '#5050cc'; }
    else if (f.type === 'wall_section') { fill = '#333'; stroke = '#111'; textFill = '#fff'; }
    const label = f.label || def?.label?.toUpperCase() || f.type.replace('_',' ').toUpperCase();
    p.push(`<g transform="translate(${f.x},${f.y}) rotate(${f.rotation} ${w/2} ${h/2})"><rect x="2" y="2" width="${w-4}" height="${h-4}" rx="4" fill="${fill}" stroke="${stroke}" stroke-width="2"/><text x="${w/2}" y="${h/2+4}" text-anchor="middle" dominant-baseline="middle" font-size="9" font-weight="700" fill="${textFill}" font-family="sans-serif">${label}</text></g>`);
  });

  room.tables.forEach((table, idx) => {
    const pos = positions[table.id] ?? { x: 60 + (idx % 6) * 130, y: 60 + Math.floor(idx / 6) * 100, rotation: 0 };
    const { w, h } = getTableDims(table.shape, table.widthCm, table.depthCm);
    const color = PRINT_COLORS[idx % PRINT_COLORS.length];
    const isRound = table.shape === 'Round' || table.shape === 'Oval';
    const seatCount = pos.seatCount ?? table.maxCapacity ?? 4;
    const coverText = `${seatCount}p`;
    const chairPos = getChairPositions(table.shape, w, h, seatCount);
    p.push(`<g transform="translate(${pos.x},${pos.y}) rotate(${pos.rotation} ${w/2} ${h/2})">`);
    // Chairs
    chairPos.forEach(cp => {
      p.push(`<rect x="${cp.cx - CHAIR_W/2}" y="${cp.cy - CHAIR_H/2}" width="${CHAIR_W}" height="${CHAIR_H}" rx="2" fill="white" stroke="${color}" stroke-width="1.5" transform="rotate(${cp.angle} ${cp.cx} ${cp.cy})"/>`);
    });
    if (isRound) p.push(`<ellipse cx="${w/2}" cy="${h/2}" rx="${w/2-2}" ry="${h/2-2}" fill="${color}" stroke="white" stroke-width="2"/>`);
    else p.push(`<rect x="2" y="2" width="${w-4}" height="${h-4}" rx="6" fill="${color}" stroke="white" stroke-width="2"/>`);
    p.push(`<text x="${w/2}" y="${h/2-3}" text-anchor="middle" dominant-baseline="middle" font-size="13" font-weight="800" fill="white" font-family="sans-serif">T${table.tableNumber}</text>`);
    p.push(`<text x="${w/2}" y="${h/2+11}" text-anchor="middle" font-size="8" fill="rgba(255,255,255,0.9)" font-family="sans-serif">${coverText}</text>`);
    p.push(`</g>`);
  });

  const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">${p.join('')}</svg>`;
  const totalCovers = room.tables.reduce((s, t) => s + (positions[t.id]?.seatCount ?? t.maxCapacity ?? 0), 0);
  const totalMin = totalCovers;
  const tableRows = room.tables.map((t, i) => {
    const c = PRINT_COLORS[i % PRINT_COLORS.length];
    const sc = positions[t.id]?.seatCount ?? t.maxCapacity;
    const covers = sc != null ? `${sc}` : '–';
    return `<tr><td><span style="display:inline-block;width:12px;height:12px;background:${c};border-radius:${t.shape==='Round'||t.shape==='Oval'?'50%':'2px'};vertical-align:middle;margin-right:6px;"></span>T${t.tableNumber}</td><td>${t.shape||'–'}</td><td style="text-align:center;">${covers}</td></tr>`;
  }).join('');
  const coverRange = totalMin < totalCovers ? `${totalMin}–${totalCovers}` : `${totalCovers}`;
  const dateStr = new Date().toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'});

  return `<!DOCTYPE html><html><head><title>Floor Plan — ${room.name}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Helvetica Neue',Arial,sans-serif;padding:24px;color:#111}h1{font-size:22px;font-weight:800;margin-bottom:2px}.sub{color:#666;font-size:13px;margin-bottom:16px}.plan-wrap{border:1px solid #ddd;border-radius:8px;overflow:hidden;max-width:100%}.legend{margin-top:20px}.legend h2{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#444;margin-bottom:8px}table{border-collapse:collapse;font-size:12px;width:auto;min-width:320px}th{text-align:left;padding:4px 10px;border-bottom:2px solid #333;font-size:11px;text-transform:uppercase;letter-spacing:.05em}td{padding:4px 10px;border-bottom:1px solid #eee}.total{font-weight:800;border-top:2px solid #333;background:#f5f5f0}@media print{body{padding:0}@page{margin:12mm}}</style>
</head><body>
<h1>The Rattlebone — ${room.name}</h1>
<p class="sub">${layoutName ? layoutName+' · ' : ''}${dateStr}${room.widthMetres&&room.lengthMetres?` · ${room.widthMetres}m × ${room.lengthMetres}m`:''}</p>
<div class="plan-wrap">${svgStr}</div>
<div class="legend"><h2>Cover Key</h2>
<table><thead><tr><th>Table</th><th>Shape</th><th>Covers</th></tr></thead>
<tbody>${tableRows}<tr class="total"><td colspan="2"><strong>TOTAL COVERS</strong></td><td style="text-align:center"><strong>${coverRange}</strong></td></tr></tbody>
</table></div></body></html>`;
}

// ─── SVG fixtures ─────────────────────────────────────────────────────────────
const FIXTURE_COLORS: Record<FixtureType, {bg:string;border:string;text:string}> = {
  door:            {bg:'hsl(var(--card))',border:'hsl(var(--primary))',text:'hsl(var(--primary))'},
  fire_exit:       {bg:'hsl(142 76% 20%)',border:'hsl(142 76% 36%)',text:'#fff'},
  accessible_exit: {bg:'hsl(221 83% 25%)',border:'hsl(221 83% 50%)',text:'#fff'},
  entrance:        {bg:'hsl(var(--primary)/0.15)',border:'hsl(var(--primary))',text:'hsl(var(--primary))'},
  staircase_up:    {bg:'hsl(var(--muted))',border:'hsl(var(--border))',text:'hsl(var(--foreground))'},
  staircase_down:  {bg:'hsl(var(--muted))',border:'hsl(var(--border))',text:'hsl(var(--foreground))'},
  bar:             {bg:'hsl(25 80% 25%)',border:'hsl(25 80% 45%)',text:'#fff'},
  kitchen:         {bg:'hsl(var(--muted))',border:'hsl(var(--border))',text:'hsl(var(--foreground))'},
  toilets:         {bg:'hsl(200 60% 20%)',border:'hsl(200 60% 40%)',text:'#fff'},
  window:          {bg:'hsl(200 100% 85% / 0.6)',border:'hsl(200 80% 50%)',text:'hsl(200 80% 30%)'},
  pillar:          {bg:'hsl(var(--muted-foreground)/0.4)',border:'hsl(var(--muted-foreground))',text:'hsl(var(--foreground))'},
  wall_section:    {bg:'hsl(var(--foreground)/0.6)',border:'hsl(var(--foreground))',text:'#fff'},
};

function FixtureSvg({ type, w, h }: { type: FixtureType; w: number; h: number }) {
  const col = FIXTURE_COLORS[type];
  const def = FIXTURE_DEFS.find(d => d.type === type);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <rect x={2} y={2} width={w-4} height={h-4} rx={4} fill={col.bg} stroke={col.border} strokeWidth={2}/>
      <text x={w/2} y={h/2+4} textAnchor="middle" dominantBaseline="middle" fontSize={9} fontWeight={700} fill={col.text}>{def?.label?.toUpperCase()}</text>
    </svg>
  );
}

function TableShape({ table, color, isSelected, seatCount }: { table: Table; color: string; isSelected: boolean; seatCount?: number }) {
  const { w, h } = getTableDims(table.shape, table.widthCm, table.depthCm);
  const isRound = table.shape === 'Round' || table.shape === 'Oval';
  const displayCount = seatCount ?? table.maxCapacity;
  return (
    <div style={{ width: w, height: h, borderRadius: isRound ? '50%' : 6,
      background: isSelected ? 'hsl(var(--primary))' : color,
      border: isSelected ? '3px solid hsl(var(--background))' : '2px solid rgba(255,255,255,0.3)',
      boxShadow: isSelected ? '0 0 0 3px hsl(var(--primary)), 0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.25)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', userSelect: 'none', cursor: 'grab',
    }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: 'white', lineHeight: 1 }}>T{table.tableNumber}</span>
      {displayCount != null && <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.85)', marginTop: 1 }}>{displayCount}p</span>}
    </div>
  );
}

function FixtureEl({ fixture, isSelected }: { fixture: Fixture; isSelected: boolean }) {
  const w = fixture.w ?? FIXTURE_DEFS.find(d => d.type === fixture.type)?.defaultW ?? 60;
  const h = fixture.h ?? FIXTURE_DEFS.find(d => d.type === fixture.type)?.defaultH ?? 50;
  return (
    <div style={{ width: w, height: h, userSelect: 'none', cursor: 'grab',
      outline: isSelected ? '2.5px solid hsl(var(--primary))' : undefined, outlineOffset: isSelected ? 3 : undefined,
      borderRadius: 6, boxShadow: isSelected ? '0 0 0 6px hsl(var(--primary)/0.2)' : undefined }}>
      <FixtureSvg type={fixture.type} w={w} h={h} />
      {fixture.label && <div style={{ textAlign: 'center', fontSize: 9, color: 'hsl(var(--foreground))', fontWeight: 700, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{fixture.label}</div>}
    </div>
  );
}

// ─── Room Canvas ──────────────────────────────────────────────────────────────
function RoomCanvas({
  room, mode, pendingFixtureType, readOnly,
  floorOutline, drawPoints, drawMousePos,
  onDirty, onFixturesDirty, onFixturePlaced,
  onAddDrawPoint, onDrawMouseMove, onClosePolygon,
  overridePositions, overrideFixtures, canvasKey,
}: {
  room: Room; mode: 'tables' | 'fixtures' | 'draw'; pendingFixtureType: FixtureType | null; readOnly: boolean;
  floorOutline: Point[]; drawPoints: Point[]; drawMousePos: Point | null;
  onDirty: (roomId: string, positions: Record<string, Pos>) => void;
  onFixturesDirty: (roomId: string, fixtures: Fixture[]) => void;
  onFixturePlaced: () => void;
  onAddDrawPoint: (p: Point) => void;
  onDrawMouseMove: (p: Point) => void;
  onClosePolygon: () => void;
  overridePositions?: Record<string, Pos>;
  overrideFixtures?: Fixture[];
  canvasKey: number;
}) {
  const [positions, setPositions] = useState<Record<string, Pos>>(() => {
    if (overridePositions) return overridePositions;
    const init: Record<string, Pos> = {};
    room.tables.forEach((t, i) => {
      init[t.id] = { x: t.xPosition ?? 60 + (i % 6) * 120, y: t.yPosition ?? 60 + Math.floor(i / 6) * 120, rotation: t.rotation ?? 0 };
    });
    return init;
  });
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [fixtures, setFixtures] = useState<Fixture[]>(() => {
    if (overrideFixtures) return overrideFixtures;
    try { return room.fixtures ? JSON.parse(room.fixtures) : []; } catch { return []; }
  });
  const [selectedFixture, setSelectedFixture] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const [labelValue, setLabelValue] = useState('');
  const dragging = useRef<{ kind: 'table' | 'fixture'; id: string; sx: number; sy: number; ox: number; oy: number } | null>(null);

  // Re-init when canvasKey changes (layout loaded)
  useEffect(() => {
    if (overridePositions) { setPositions(overridePositions); return; }
    const init: Record<string, Pos> = {};
    room.tables.forEach((t, i) => {
      init[t.id] = { x: t.xPosition ?? 60 + (i % 6) * 120, y: t.yPosition ?? 60 + Math.floor(i / 6) * 120, rotation: t.rotation ?? 0 };
    });
    setPositions(init);
    if (overrideFixtures) setFixtures(overrideFixtures);
    else { try { setFixtures(room.fixtures ? JSON.parse(room.fixtures) : []); } catch { setFixtures([]); } }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasKey]);

  const emitFixtures = useCallback((next: Fixture[]) => {
    setFixtures(next); onFixturesDirty(room.id, next);
  }, [room.id, onFixturesDirty]);

  useEffect(() => {
    if (readOnly) return;
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const { kind, id, sx, sy, ox, oy } = dragging.current;
      if (kind === 'table') {
        const { w, h } = getTableDims(room.tables.find(t => t.id === id)?.shape);
        setPositions(prev => ({ ...prev, [id]: { ...prev[id], x: Math.max(0, Math.min(CANVAS_W - w, ox + e.clientX - sx)), y: Math.max(0, Math.min(CANVAS_H - h, oy + e.clientY - sy)) } }));
      } else {
        setFixtures(prev => prev.map(f => f.id !== id ? f : { ...f, x: Math.max(0, Math.min(CANVAS_W - (f.w ?? 60), ox + e.clientX - sx)), y: Math.max(0, Math.min(CANVAS_H - (f.h ?? 50), oy + e.clientY - sy)) }));
      }
    };
    const onUp = () => {
      if (dragging.current) {
        if (dragging.current.kind === 'fixture') setFixtures(prev => { onFixturesDirty(room.id, prev); return prev; });
        else setPositions(prev => { onDirty(room.id, prev); return prev; });
      }
      dragging.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [room, readOnly, onDirty, onFixturesDirty]);

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mode === 'draw') return; // SVG handles clicks in draw mode
    if (pendingFixtureType && !readOnly) {
      const rect = e.currentTarget.getBoundingClientRect();
      const def = FIXTURE_DEFS.find(d => d.type === pendingFixtureType)!;
      const newF: Fixture = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: pendingFixtureType,
        x: Math.max(0, Math.min(CANVAS_W - def.defaultW, e.clientX - rect.left - def.defaultW / 2)),
        y: Math.max(0, Math.min(CANVAS_H - def.defaultH, e.clientY - rect.top - def.defaultH / 2)),
        rotation: 0, w: def.defaultW, h: def.defaultH,
      };
      emitFixtures([...fixtures, newF]);
      setSelectedFixture(newF.id);
      onFixturePlaced();
      return;
    }
    setSelectedTable(null); setSelectedFixture(null);
  };

  const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (mode !== 'draw') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const p: Point = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    if (drawPoints.length >= 3 && dist(p, drawPoints[0]) < CLOSE_THRESHOLD) {
      onClosePolygon();
    } else {
      onAddDrawPoint(p);
    }
  };

  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (mode !== 'draw') return;
    const rect = e.currentTarget.getBoundingClientRect();
    onDrawMouseMove({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleSvgDblClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (mode !== 'draw') return;
    e.preventDefault();
    if (drawPoints.length >= 3) onClosePolygon();
  };

  const handleTableMouseDown = (e: React.MouseEvent, id: string) => {
    if (readOnly) return;
    e.preventDefault(); e.stopPropagation();
    setSelectedTable(id); setSelectedFixture(null);
    dragging.current = { kind: 'table', id, sx: e.clientX, sy: e.clientY, ox: positions[id]?.x ?? 0, oy: positions[id]?.y ?? 0 };
  };

  const handleFixtureMouseDown = (e: React.MouseEvent, id: string) => {
    if (readOnly) return;
    e.preventDefault(); e.stopPropagation();
    setSelectedFixture(id); setSelectedTable(null);
    const f = fixtures.find(x => x.id === id);
    if (!f) return;
    dragging.current = { kind: 'fixture', id, sx: e.clientX, sy: e.clientY, ox: f.x, oy: f.y };
  };

  const rotateTable = (id: string) => {
    setPositions(prev => { const updated = { ...prev, [id]: { ...prev[id], rotation: ((prev[id]?.rotation ?? 0) + 90) % 360 } }; onDirty(room.id, updated); return updated; });
  };

  const removeChair = (tableId: string) => {
    setPositions(prev => {
      const cur = prev[tableId] ?? { x: 0, y: 0, rotation: 0 };
      const table = room.tables.find(t => t.id === tableId);
      const curCount = cur.seatCount ?? table?.maxCapacity ?? 4;
      const updated = { ...prev, [tableId]: { ...cur, seatCount: Math.max(0, curCount - 1) } };
      onDirty(room.id, updated);
      return updated;
    });
  };

  const addChair = (tableId: string) => {
    setPositions(prev => {
      const cur = prev[tableId] ?? { x: 0, y: 0, rotation: 0 };
      const table = room.tables.find(t => t.id === tableId);
      const curCount = cur.seatCount ?? table?.maxCapacity ?? 4;
      const updated = { ...prev, [tableId]: { ...cur, seatCount: curCount + 1 } };
      onDirty(room.id, updated);
      return updated;
    });
  };
  const rotateFixture = (id: string) => emitFixtures(fixtures.map(f => f.id !== id ? f : { ...f, rotation: (f.rotation + 90) % 360 }));
  const deleteFixture = (id: string) => { emitFixtures(fixtures.filter(f => f.id !== id)); setSelectedFixture(null); };
  const startEditLabel = (id: string) => { const f = fixtures.find(x => x.id === id); setLabelValue(f?.label || ''); setEditingLabel(id); };
  const saveLabel = () => { if (!editingLabel) return; emitFixtures(fixtures.map(f => f.id !== editingLabel ? f : { ...f, label: labelValue.trim() || undefined })); setEditingLabel(null); };

  const selFixture = selectedFixture ? fixtures.find(f => f.id === selectedFixture) : null;
  const selTable = selectedTable ? room.tables.find(t => t.id === selectedTable) : null;
  const roomOutline = (room.widthMetres && room.lengthMetres)
    ? { w: Math.min(CANVAS_W - 40, room.widthMetres * 60), h: Math.min(CANVAS_H - 40, room.lengthMetres * 60) }
    : null;

  // Near-first-point indicator for draw mode
  const nearFirst = mode === 'draw' && drawPoints.length >= 3 && drawMousePos && dist(drawMousePos, drawPoints[0]) < CLOSE_THRESHOLD;

  return (
    <div className="space-y-2">
      {!readOnly && (selFixture || selTable) && (
        <div className="flex items-center gap-2 flex-wrap bg-muted/40 border border-border rounded-lg px-3 py-2">
          {selTable && (<>
            <span className="text-xs font-semibold text-foreground">Table {selTable.tableNumber}</span>
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => rotateTable(selectedTable!)}><RotateCw className="w-3 h-3" /> Rotate</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedTable(null)}><X className="w-3 h-3" /> Deselect</Button>
          </>)}
          {selFixture && (<>
            <span className="text-xs font-semibold text-foreground">{FIXTURE_DEFS.find(d => d.type === selFixture.type)?.label}</span>
            {editingLabel === selFixture.id ? (
              <div className="flex items-center gap-1.5">
                <Input value={labelValue} onChange={e => setLabelValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveLabel()} className="h-7 w-28 text-xs" placeholder="Custom label" autoFocus />
                <Button size="sm" className="h-7 text-xs" onClick={saveLabel}>Save</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => startEditLabel(selFixture.id)}>{selFixture.label ? `Label: ${selFixture.label}` : 'Add Label'}</Button>
            )}
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => rotateFixture(selFixture.id)}><RotateCw className="w-3 h-3" /> Rotate</Button>
            <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-destructive hover:text-destructive" onClick={() => deleteFixture(selFixture.id)}><Trash2 className="w-3 h-3" /> Remove</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedFixture(null)}><X className="w-3 h-3" /> Deselect</Button>
          </>)}
        </div>
      )}

      <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <div style={{
          position: 'relative', width: CANVAS_W, height: CANVAS_H,
          background: room.floorPlanUrl ? `url(${room.floorPlanUrl}) center/cover` : 'radial-gradient(ellipse at 30% 20%, hsl(var(--muted)/0.6) 0%, hsl(var(--background)) 70%)',
          border: '2px solid hsl(var(--border))', borderRadius: 10,
          cursor: mode === 'draw' ? 'crosshair' : pendingFixtureType ? 'crosshair' : 'default',
        }} onClick={handleCanvasClick}>

          {/* SVG overlay — grid + outlines + draw tool */}
          <svg
            style={{ position: 'absolute', inset: 0, pointerEvents: mode === 'draw' ? 'auto' : 'none' }}
            width={CANVAS_W} height={CANVAS_H}
            onClick={handleSvgClick} onMouseMove={handleSvgMouseMove} onDoubleClick={handleSvgDblClick}
          >
            {/* Grid */}
            {Array.from({ length: Math.ceil(CANVAS_W / 40) + 1 }, (_, i) => <line key={`v${i}`} x1={i*40} y1={0} x2={i*40} y2={CANVAS_H} stroke="currentColor" strokeWidth={0.5} opacity={0.08}/>)}
            {Array.from({ length: Math.ceil(CANVAS_H / 40) + 1 }, (_, i) => <line key={`h${i}`} x1={0} y1={i*40} x2={CANVAS_W} y2={i*40} stroke="currentColor" strokeWidth={0.5} opacity={0.08}/>)}

            {/* Saved floor outline polygon */}
            {floorOutline.length > 2 && (
              <polygon points={floorOutline.map(pp => `${pp.x},${pp.y}`).join(' ')}
                fill="hsl(var(--foreground)/0.04)" stroke="hsl(var(--foreground)/0.35)" strokeWidth={3} strokeLinejoin="round"/>
            )}

            {/* Fallback rectangular outline from dimensions */}
            {floorOutline.length === 0 && roomOutline && (
              <rect x={20} y={20} width={roomOutline.w} height={roomOutline.h}
                fill="hsl(var(--foreground)/0.02)" stroke="hsl(var(--foreground)/0.2)" strokeWidth={2} rx={4}/>
            )}

            {/* Draw-in-progress */}
            {mode === 'draw' && drawPoints.length > 0 && (<>
              {drawPoints.slice(1).map((pp, i) => <line key={i} x1={drawPoints[i].x} y1={drawPoints[i].y} x2={pp.x} y2={pp.y} stroke="hsl(var(--primary))" strokeWidth={2}/>)}
              {drawMousePos && (
                <line x1={drawPoints[drawPoints.length-1].x} y1={drawPoints[drawPoints.length-1].y}
                  x2={drawMousePos.x} y2={drawMousePos.y} stroke="hsl(var(--primary))" strokeWidth={1.5} strokeDasharray="6 3" opacity={0.7}/>
              )}
              {drawPoints.map((pp, i) => (
                <circle key={i} cx={pp.x} cy={pp.y} r={i === 0 ? 9 : 5}
                  fill={i === 0 ? (nearFirst ? 'hsl(142 70% 45%)' : 'hsl(var(--primary))') : 'white'}
                  stroke={i === 0 ? (nearFirst ? 'hsl(142 70% 45%)' : 'hsl(var(--primary))') : 'hsl(var(--primary))'}
                  strokeWidth={2}/>
              ))}
              {nearFirst && drawMousePos && (
                <text x={drawPoints[0].x + 12} y={drawPoints[0].y - 10} fontSize={11} fill="hsl(142 70% 45%)" fontWeight={700}>click to close</text>
              )}
            </>)}
          </svg>

          {/* Placement hint */}
          {(pendingFixtureType || mode === 'draw') && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 12, pointerEvents: 'none', zIndex: 20 }}>
              <div style={{ background: 'hsl(var(--card)/0.9)', border: '1.5px dashed hsl(var(--primary))', borderRadius: 8, padding: '6px 14px', fontSize: 11, color: 'hsl(var(--primary))', fontWeight: 600 }}>
                {mode === 'draw'
                  ? drawPoints.length === 0 ? 'Click to place first corner point' : drawPoints.length < 3 ? `${drawPoints.length} point${drawPoints.length===1?'':'s'} — keep clicking to draw outline` : 'Double-click or click first point (green) to close'
                  : `Click to place ${FIXTURE_DEFS.find(d => d.type === pendingFixtureType)?.label}`}
              </div>
            </div>
          )}

          {/* Read-only overlay */}
          {readOnly && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 30 }}/>
          )}

          {/* Fixtures */}
          {fixtures.map(f => (
            <div key={f.id} style={{ position: 'absolute', left: f.x, top: f.y, transform: `rotate(${f.rotation}deg)`, transformOrigin: 'center', zIndex: selectedFixture === f.id ? 15 : 5, cursor: readOnly ? 'default' : 'grab' }}
              onMouseDown={e => { e.stopPropagation(); if (!readOnly) handleFixtureMouseDown(e, f.id); }}
              onClick={e => { e.stopPropagation(); if (!readOnly) { setSelectedFixture(f.id); setSelectedTable(null); } }}>
              <FixtureEl fixture={f} isSelected={selectedFixture === f.id} />
            </div>
          ))}

          {/* Tables with chairs */}
          {room.tables.map((table, idx) => {
            const pos = positions[table.id] ?? { x: 60, y: 60, rotation: 0 };
            const { w, h } = getTableDims(table.shape, table.widthCm, table.depthCm);
            const seatCount = pos.seatCount ?? table.maxCapacity ?? 4;
            const chairPositions = getChairPositions(table.shape, w, h, seatCount);
            const color = TABLE_COLORS[idx % TABLE_COLORS.length];
            return (
              <div key={table.id} style={{ position: 'absolute', left: pos.x - CHAIR_AREA, top: pos.y - CHAIR_AREA, transform: `rotate(${pos.rotation}deg)`, transformOrigin: `${w/2 + CHAIR_AREA}px ${h/2 + CHAIR_AREA}px`, zIndex: selectedTable === table.id ? 10 : 3, pointerEvents: 'none' }}>
                {/* Chairs */}
                {chairPositions.map((cp, ci) => (
                  <div key={ci} style={{
                    position: 'absolute',
                    left: CHAIR_AREA + cp.cx - CHAIR_W / 2,
                    top: CHAIR_AREA + cp.cy - CHAIR_H / 2,
                    width: CHAIR_W, height: CHAIR_H,
                    background: 'hsl(var(--card))',
                    border: `1.5px solid ${color}`,
                    borderRadius: 2,
                    transform: `rotate(${cp.angle}deg)`,
                    transformOrigin: 'center',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    cursor: readOnly ? 'default' : 'pointer',
                    pointerEvents: 'auto',
                  }}
                  onClick={e => { e.stopPropagation(); if (!readOnly) removeChair(table.id); }}
                  title="Click to remove chair"
                  />
                ))}
                {/* Table shape */}
                <div style={{ position: 'absolute', left: CHAIR_AREA, top: CHAIR_AREA, cursor: readOnly ? 'default' : 'grab', pointerEvents: 'auto' }}
                  onMouseDown={e => { e.stopPropagation(); if (!readOnly) handleTableMouseDown(e, table.id); }}>
                  <TableShape table={table} color={color} isSelected={selectedTable === table.id} seatCount={seatCount} />
                </div>
                {/* Add chair button when selected */}
                {selectedTable === table.id && !readOnly && (
                  <button style={{ position: 'absolute', top: CHAIR_AREA - 11, right: CHAIR_AREA - 11, background: 'hsl(var(--primary))', color: 'white', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 25, boxShadow: '0 2px 4px rgba(0,0,0,0.3)', pointerEvents: 'auto' }}
                    onClick={e => { e.stopPropagation(); addChair(table.id); }} title="Add chair">+</button>
                )}
              </div>
            );
          })}

          {room.tables.length === 0 && fixtures.length === 0 && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 13 }}>Add tables or place fixtures to begin</p>
            </div>
          )}
        </div>
      </div>

      {room.tables.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 items-center">
          {room.tables.map((t, i) => {
            const sc = positions[t.id]?.seatCount ?? t.maxCapacity;
            return (
              <div key={t.id} className="flex items-center gap-1.5 text-xs">
                <div className="w-3 h-3 rounded-sm" style={{ background: TABLE_COLORS[i % TABLE_COLORS.length] }}/>
                <span className="text-muted-foreground">T{t.tableNumber}{sc != null ? ` (${sc}p)` : ''}</span>
              </div>
            );
          })}
          <span className="text-xs font-semibold text-foreground ml-2">
            Total: {room.tables.reduce((s, t) => s + (positions[t.id]?.seatCount ?? t.maxCapacity ?? 0), 0)} covers
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function TablePlannerCanvas({
  canSaveCanonical = true,
  showCreateLayoutFlow = false,
  defaultLayoutId,
}: {
  canSaveCanonical?: boolean;
  showCreateLayoutFlow?: boolean;
  defaultLayoutId?: string;
}) {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'tables' | 'fixtures' | 'draw'>('tables');
  const [pendingFixture, setPendingFixture] = useState<FixtureType | null>(null);
  const [activeRoomId, setActiveRoomId] = useState('');
  const [isViewMode, setIsViewMode] = useState(showCreateLayoutFlow);
  const [canvasKey, setCanvasKey] = useState(0);

  // Draw state
  const [drawPoints, setDrawPoints] = useState<Point[]>([]);
  const [drawMousePos, setDrawMousePos] = useState<Point | null>(null);
  const [floorOutlines, setFloorOutlines] = useState<Record<string, Point[]>>({});

  const [settingDefault, setSettingDefault] = useState(false);

  // Named layouts
  const [savedLayouts, setSavedLayouts] = useState<LayoutWithDefault[]>([]);
  const [activeLayoutId, setActiveLayoutId] = useState<string>('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [layoutName, setLayoutName] = useState('');
  const [layoutEventDate, setLayoutEventDate] = useState('');
  const [layoutNotes, setLayoutNotes] = useState('');
  const [savingLayout, setSavingLayout] = useState(false);

  // Email
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailNotes, setEmailNotes] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  // AI layout
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<AiRoomLayoutOutputType | null>(null);

  // Layout override (when loading a saved layout)
  const [overridePositions, setOverridePositions] = useState<Record<string, Pos> | undefined>();
  const [overrideFixtures, setOverrideFixtures] = useState<Fixture[] | undefined>();

  const dirtyPositions = useRef<Record<string, Record<string, Pos>>>({});
  const dirtyFixtures = useRef<Record<string, Fixture[]>>({});

  useEffect(() => {
    Promise.all([
      getTablePlannerData({}).then(d => {
        setRooms(d.rooms);
        if (d.rooms.length > 0) setActiveRoomId(d.rooms[0].id);
        // Load saved outlines from room data
        const outlines: Record<string, Point[]> = {};
        d.rooms.forEach(r => {
          if (r.floorOutline) { try { outlines[r.id] = JSON.parse(r.floorOutline); } catch {} }
        });
        setFloorOutlines(outlines);
      }),
      getRoomLayouts({}).then(d => setSavedLayouts(d.layouts)),
    ]).finally(() => setLoading(false));
  }, []);

  // ESC cancels drawing
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mode === 'draw') { setDrawPoints([]); setDrawMousePos(null); setMode('tables'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode]);

  const handleDirty = (roomId: string, positions: Record<string, Pos>) => { dirtyPositions.current[roomId] = positions; };
  const handleFixturesDirty = (roomId: string, fx: Fixture[]) => { dirtyFixtures.current[roomId] = fx; };

  // Reconstruct current state for active room
  const getCurrentState = useCallback(() => {
    const room = rooms.find(r => r.id === activeRoomId);
    if (!room) return null;
    const dirty = dirtyPositions.current[room.id];
    const positions: Record<string, Pos> = {};
    room.tables.forEach((t, i) => {
      positions[t.id] = dirty?.[t.id] ?? { x: t.xPosition ?? 60 + (i % 6) * 120, y: t.yPosition ?? 60 + Math.floor(i / 6) * 120, rotation: t.rotation ?? 0 };
    });
    const fx = dirtyFixtures.current[room.id] ?? (() => { try { return room.fixtures ? JSON.parse(room.fixtures) : []; } catch { return []; } })();
    const outline = floorOutlines[room.id] ?? [];
    return { room, positions, fixtures: fx, outline };
  }, [rooms, activeRoomId, floorOutlines]);

  const handleSaveCanonical = async () => {
    const tablePositions = Object.values(dirtyPositions.current).flatMap(rp => Object.entries(rp).map(([id, pos]) => ({ id, xPosition: Math.round(pos.x), yPosition: Math.round(pos.y), rotation: pos.rotation })));
    const fixturesSaves = Object.entries(dirtyFixtures.current).map(([roomId, fx]) => saveRoomFixtures({ roomId, fixtures: JSON.stringify(fx) }));
    const outlineSaves = Object.entries(floorOutlines).map(([roomId, pts]) => saveRoomFixtures({ roomId, floorOutline: JSON.stringify(pts) }));
    if (tablePositions.length === 0 && fixturesSaves.length === 0 && outlineSaves.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      await Promise.all([
        ...(tablePositions.length > 0 ? [saveTablePositions({ positions: tablePositions })] : []),
        ...fixturesSaves, ...outlineSaves,
      ]);
      toast.success('Layout saved');
      dirtyPositions.current = {}; dirtyFixtures.current = {};
    } catch (e: any) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSaveNamedLayout = async () => {
    if (!layoutName.trim()) { toast.error('Enter a layout name'); return; }
    const state = getCurrentState();
    if (!state) return;
    setSavingLayout(true);
    try {
      const result = await saveRoomLayout({
        id: activeLayoutId || undefined,
        name: layoutName,
        roomId: state.room.id,
        tablePositions: JSON.stringify(state.positions),
        fixtures: JSON.stringify(state.fixtures),
        floorOutline: JSON.stringify(state.outline),
        eventDate: layoutEventDate || undefined,
        notes: layoutNotes || undefined,
      });
      toast.success(`Layout "${layoutName}" saved`);
      setActiveLayoutId(result.id);
      setSaveOpen(false);
      getRoomLayouts({}).then(d => setSavedLayouts(d.layouts));
    } catch (e: any) { toast.error(e.message || 'Failed to save layout'); }
    finally { setSavingLayout(false); }
  };

  const loadLayout = (layout: LayoutWithDefault) => {
    try {
      const positions = JSON.parse(layout.tablePositions) as Record<string, Pos>;
      const fixtures = JSON.parse(layout.fixtures) as Fixture[];
      const outline: Point[] = layout.floorOutline ? JSON.parse(layout.floorOutline) : [];
      setOverridePositions(positions);
      setOverrideFixtures(fixtures);
      setFloorOutlines(prev => ({ ...prev, [layout.roomId]: outline }));
      setActiveRoomId(layout.roomId);
      setActiveLayoutId(layout.id);
      setLayoutName(layout.name);
      setLayoutEventDate(layout.eventDate || '');
      setLayoutNotes(layout.notes || '');
      dirtyPositions.current = {};
      dirtyFixtures.current = {};
      setCanvasKey(k => k + 1);
      toast.success(`Loaded: ${layout.name}`);
    } catch { toast.error('Failed to load layout'); }
  };

  // Auto-load default layout on startup if provided
  useEffect(() => {
    if (defaultLayoutId && savedLayouts.length > 0) {
      const def = savedLayouts.find(l => l.id === defaultLayoutId);
      if (def) loadLayout(def);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLayoutId, savedLayouts.length]);

  const handleSetDefault = async (layout: LayoutWithDefault) => {
    const isCurrentlyDefault = layout.isDefault;
    setSettingDefault(true);
    try {
      await setDefaultLayout({
        layoutId: layout.id,
        roomId: layout.roomId,
        setAsDefault: !isCurrentlyDefault,
      });
      setSavedLayouts(prev => prev.map(l => ({
        ...l,
        isDefault: l.roomId === layout.roomId
          ? (l.id === layout.id ? !isCurrentlyDefault : false)
          : l.isDefault,
      })));
      toast.success(isCurrentlyDefault ? 'Live plan cleared' : `"${layout.name}" is now the live plan`);
    } catch (e: any) {
      toast.error(e.message || 'Failed to update live plan');
    } finally {
      setSettingDefault(false);
    }
  };

  const handlePrint = () => {
    const state = getCurrentState();
    if (!state) return;
    const html = buildPrintHTML(state.room, state.positions, state.fixtures, state.outline, layoutName);
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const handleEmail = async () => {
    if (!emailTo.trim()) { toast.error('Enter a recipient email'); return; }
    const state = getCurrentState();
    if (!state) return;
    setEmailSending(true);
    try {
      await emailFloorPlan({
        toEmail: emailTo,
        roomName: state.room.name,
        layoutName: layoutName || 'Floor Plan',
        tableRows: state.room.tables.map(t => ({ tableNumber: t.tableNumber, shape: t.shape, minCapacity: t.minCapacity, maxCapacity: t.maxCapacity })),
        totalCovers: state.room.tables.reduce((s, t) => s + (t.maxCapacity ?? 0), 0),
        eventDate: layoutEventDate || undefined,
        notes: emailNotes || layoutNotes || undefined,
      });
      toast.success('Floor plan emailed');
      setEmailOpen(false);
      setEmailTo('');
    } catch (e: any) { toast.error(e.message || 'Failed to send email'); }
    finally { setEmailSending(false); }
  };

  const handleAiLayout = async () => {
    if (!aiPrompt.trim()) { toast.error('Describe the event first'); return; }
    const room = rooms.find(r => r.id === activeRoomId);
    if (!room) return;
    setAiLoading(true); setAiResult(null);
    try {
      const result = await aiRoomLayout({
        eventDescription: aiPrompt,
        roomName: room.name,
        roomWidth: room.widthMetres,
        roomLength: room.lengthMetres,
        fixtures: room.fixtures,
        floorOutline: floorOutlines[room.id] ? JSON.stringify(floorOutlines[room.id]) : undefined,
        existingTables: room.tables.map(t => {
          const { w, h } = getTableDims(t.shape, t.widthCm, t.depthCm);
          return { tableNumber: t.tableNumber, shape: t.shape || '', maxCapacity: t.maxCapacity ?? null, widthPx: w, heightPx: h };
        }),
      });
      setAiResult(result);
      // Apply AI positions to canvas
      if (result.tableArrangement.length > 0) {
        const newPositions: Record<string, Pos> = {};
        result.tableArrangement.forEach(arrangement => {
          const table = room.tables.find(t => t.tableNumber === arrangement.tableNumber);
          if (table) newPositions[table.id] = { x: arrangement.x, y: arrangement.y, rotation: 0 };
        });
        // Preserve positions for tables AI didn't mention
        const dirty = dirtyPositions.current[room.id] ?? {};
        room.tables.forEach((t, i) => {
          if (!newPositions[t.id]) newPositions[t.id] = dirty[t.id] ?? { x: t.xPosition ?? 60 + (i % 6) * 120, y: t.yPosition ?? 60 + Math.floor(i / 6) * 120, rotation: 0 };
        });
        setOverridePositions(newPositions);
        setCanvasKey(k => k + 1);
        dirtyPositions.current[room.id] = newPositions;
      }
    } catch (e: any) { toast.error(e.message || 'AI layout failed'); }
    finally { setAiLoading(false); }
  };

  const handleAddDrawPoint = (p: Point) => setDrawPoints(prev => [...prev, p]);
  const handleDrawMouseMove = (p: Point) => setDrawMousePos(p);
  const handleClosePolygon = () => {
    if (drawPoints.length >= 3) {
      setFloorOutlines(prev => ({ ...prev, [activeRoomId]: [...drawPoints] }));
      toast.success('Room outline drawn — save to keep it');
    }
    setDrawPoints([]); setDrawMousePos(null); setMode('tables');
  };

  const clearOutline = () => {
    setFloorOutlines(prev => ({ ...prev, [activeRoomId]: [] }));
    toast.info('Outline cleared — save to persist');
  };

  const roomLayouts = savedLayouts.filter(l => l.roomId === activeRoomId);
  const activeRoom = rooms.find(r => r.id === activeRoomId);

  if (loading) return <div className="p-8 text-center text-muted-foreground text-sm">Loading planner...</div>;

  return (
    <div className="space-y-4">
      {/* Live Plan Banner (managers, when a layout is loaded and is default) */}
      {!isViewMode && canSaveCanonical && activeLayoutId && savedLayouts.find(l => l.id === activeLayoutId)?.isDefault && (
        <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Live Plan</span>
          </div>
          <span className="text-xs text-muted-foreground">
            <strong className="text-foreground">{savedLayouts.find(l => l.id === activeLayoutId)?.name}</strong> is the current default — staff see this when they open Floor Plans.
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 text-xs ml-auto text-muted-foreground hover:text-destructive"
            onClick={() => {
              const al = savedLayouts.find(l => l.id === activeLayoutId);
              if (al) handleSetDefault(al);
            }}
            disabled={settingDefault}
          >
            Remove Live
          </Button>
        </div>
      )}

      {/* View Mode Banner (staff) */}
      {isViewMode && (
        <div className="flex items-center justify-between gap-3 bg-muted/40 border border-border rounded-lg px-4 py-3 flex-wrap">
          <div>
            {activeLayoutId && savedLayouts.find(l => l.id === activeLayoutId)?.isDefault ? (
              <>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <p className="text-sm font-semibold text-foreground">
                    Live Plan — {savedLayouts.find(l => l.id === activeLayoutId)?.name}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">This is the current live layout set by your manager.</p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-foreground">Viewing Base Layout</p>
                <p className="text-xs text-muted-foreground">This is the default room layout. Create a new layout to adjust for your event.</p>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1.5"><Printer className="w-3.5 h-3.5"/> Print</Button>
            <Button size="sm" className="gap-1.5 bg-primary text-primary-foreground" onClick={() => { setIsViewMode(false); setMode('tables'); }}>
              <BookmarkPlus className="w-3.5 h-3.5"/> Create New Layout
            </Button>
          </div>
        </div>
      )}

      {/* Top toolbar */}
      {!isViewMode && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Mode buttons */}
            <div className="flex rounded-lg border border-border overflow-hidden">
              {(['tables','fixtures','draw'] as const).map(m => (
                <button key={m} onClick={() => { setMode(m); setPendingFixture(null); if (m !== 'draw') { setDrawPoints([]); setDrawMousePos(null); } }}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
                  {m === 'tables' ? '⊞ Tables' : m === 'fixtures' ? '⬛ Elements' : '✏️ Draw Outline'}
                </button>
              ))}
            </div>
            {mode === 'draw' && (<>
              {(floorOutlines[activeRoomId] ?? []).length > 0 && (
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-destructive" onClick={clearOutline}><Eraser className="w-3 h-3"/> Clear Outline</Button>
              )}
              {drawPoints.length > 0 && (
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setDrawPoints([]); setDrawMousePos(null); }}>Cancel Draw</Button>
              )}
            </>)}
            {mode === 'tables' && (
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => setAiOpen(true)}><Sparkles className="w-3 h-3"/> AI Layout</Button>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            {/* Load layout */}
            {roomLayouts.length > 0 && (
              <Select value="" onValueChange={id => { const l = savedLayouts.find(s => s.id === id); if (l) loadLayout(l); }}>
                <SelectTrigger className="h-8 w-44 text-xs gap-1"><FolderOpen className="w-3 h-3 text-muted-foreground"/><span className="text-muted-foreground">Load Layout…</span></SelectTrigger>
                <SelectContent>
                  {roomLayouts.map(l => (
                    <SelectItem key={l.id} value={l.id}>
                      <span className="flex items-center gap-1.5">
                        {l.isDefault && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-400 px-1.5 py-0.5 rounded-full uppercase tracking-wide">● LIVE</span>}
                        <span>{l.name}{l.eventDate ? ` · ${new Date(l.eventDate).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}` : ''}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handlePrint}><Printer className="w-3.5 h-3.5"/> Print</Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEmailOpen(true)}><Mail className="w-3.5 h-3.5"/> Email</Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setSaveOpen(true); }}><BookmarkPlus className="w-3.5 h-3.5"/> Save Layout</Button>
            {canSaveCanonical && activeLayoutId && (() => {
              const al = savedLayouts.find(l => l.id === activeLayoutId);
              return al ? (
                <Button
                  onClick={() => handleSetDefault(al)}
                  disabled={settingDefault}
                  variant={al.isDefault ? 'outline' : 'default'}
                  className={`gap-1.5 shrink-0 ${al.isDefault ? 'border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                >
                  {settingDefault
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Updating…</>
                    : al.isDefault
                      ? <><Radio className="w-3.5 h-3.5"/> Live Plan ✓</>
                      : <><RadioTower className="w-3.5 h-3.5"/> Make Live</>}
                </Button>
              ) : null;
            })()}
            {canSaveCanonical && (
              <Button onClick={handleSaveCanonical} disabled={saving} className="gap-1.5 shrink-0">
                <Save className="w-3.5 h-3.5"/> {saving ? 'Saving…' : 'Save Canonical'}
              </Button>
            )}
            {showCreateLayoutFlow && (
              <Button variant="outline" size="sm" onClick={() => setIsViewMode(true)}>← Back to View</Button>
            )}
          </div>
        </div>
      )}

      {/* Fixture palette */}
      {!isViewMode && mode === 'fixtures' && (
        <div className="bg-card border border-border rounded-xl p-3">
          <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-2">Room Elements</p>
          <div className="flex flex-wrap gap-2">
            {FIXTURE_DEFS.map(def => (
              <button key={def.type} onClick={() => setPendingFixture(pendingFixture === def.type ? null : def.type)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${pendingFixture === def.type ? 'bg-primary text-primary-foreground border-primary shadow-md scale-105' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground hover:bg-muted/50'}`}>
                <span>{def.icon}</span><span>{def.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Canvas tabs */}
      {rooms.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-dashed rounded-xl">
          <Info className="w-8 h-8 mx-auto mb-3 opacity-30"/>
          <p className="text-sm">Add rooms and tables in Admin → Venue first.</p>
        </div>
      ) : (
        <Tabs value={activeRoomId} onValueChange={id => { setActiveRoomId(id); setOverridePositions(undefined); setOverrideFixtures(undefined); }}>
          <TabsList>{rooms.map(r => <TabsTrigger key={r.id} value={r.id}>{r.name} ({r.tables.length})</TabsTrigger>)}</TabsList>
          {rooms.map(r => (
            <TabsContent key={r.id} value={r.id}>
              <RoomCanvas
                key={`${r.id}-${canvasKey}`}
                room={r} mode={isViewMode ? 'tables' : mode}
                pendingFixtureType={isViewMode ? null : pendingFixture}
                readOnly={isViewMode}
                floorOutline={floorOutlines[r.id] ?? []}
                drawPoints={activeRoomId === r.id ? drawPoints : []}
                drawMousePos={activeRoomId === r.id ? drawMousePos : null}
                onDirty={handleDirty} onFixturesDirty={handleFixturesDirty}
                onFixturePlaced={() => {}}
                onAddDrawPoint={handleAddDrawPoint} onDrawMouseMove={handleDrawMouseMove} onClosePolygon={handleClosePolygon}
                overridePositions={activeRoomId === r.id ? overridePositions : undefined}
                overrideFixtures={activeRoomId === r.id ? overrideFixtures : undefined}
                canvasKey={canvasKey}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* AI Result notice */}
      {aiResult && !aiLoading && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0"/>
          <div><p className="text-xs font-semibold text-foreground mb-0.5">AI Layout Applied</p><p className="text-xs text-muted-foreground">{aiResult.suggestion}</p></div>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 ml-auto shrink-0" onClick={() => setAiResult(null)}><X className="w-3 h-3"/></Button>
        </div>
      )}

      {/* Save Named Layout Dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Save Layout</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Layout Name *</label>
              <Input value={layoutName} onChange={e => setLayoutName(e.target.value)} placeholder="e.g. Wedding 14th June — 80 covers" autoFocus/>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Event Date (optional)</label>
              <Input type="date" value={layoutEventDate} onChange={e => setLayoutEventDate(e.target.value)}/>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Notes (optional)</label>
              <Textarea value={layoutNotes} onChange={e => setLayoutNotes(e.target.value)} rows={2} placeholder="Any notes about this layout…"/>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveNamedLayout} disabled={savingLayout || !layoutName.trim()} className="flex-1">
                {savingLayout ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin"/>Saving…</> : <><BookmarkPlus className="w-3.5 h-3.5 mr-2"/>Save Layout</>}
              </Button>
              <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4"/> Email Floor Plan</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Recipient Email *</label>
              <Input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="recipient@example.com" autoFocus/>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Additional Notes</label>
              <Textarea value={emailNotes} onChange={e => setEmailNotes(e.target.value)} rows={2} placeholder="Any notes for the recipient…"/>
            </div>
            {activeRoom && (
              <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
                Will send: <strong>{activeRoom.name}</strong>{layoutName ? ` — ${layoutName}` : ''} with {activeRoom.tables.reduce((s,t) => s + (t.maxCapacity ?? 0), 0)} total covers
              </p>
            )}
            <div className="flex gap-2">
              <Button onClick={handleEmail} disabled={emailSending || !emailTo.trim()} className="flex-1">
                {emailSending ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin"/>Sending…</> : <><Mail className="w-3.5 h-3.5 mr-2"/>Send Email</>}
              </Button>
              <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Layout Dialog */}
      <Dialog open={aiOpen} onOpenChange={open => { setAiOpen(open); if (!open) setAiResult(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary"/> AI Layout Assistant</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">Describe your event and AI will arrange the tables on the canvas for you.</p>
            <Textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder='e.g. "80 guests, wedding reception, round tables of 8 for dinner"' className="min-h-20"/>
            <Button onClick={handleAiLayout} disabled={aiLoading} className="w-full bg-primary text-primary-foreground">
              {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Arranging tables…</> : <><Sparkles className="w-4 h-4 mr-2"/>Generate & Apply Layout</>}
            </Button>
            {aiResult && (
              <div className="text-xs bg-muted/40 rounded-lg p-3 space-y-1">
                <p className="font-medium text-foreground">{aiResult.suggestion}</p>
                {aiResult.tableArrangement.map((t, i) => <p key={i} className="text-muted-foreground">T{t.tableNumber}: {t.notes}</p>)}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
